/**
 * Central SPEC §14 colour semantics + bot-domain label maps. One place so
 * every surface renders bias / role / protection / system-state identically.
 *
 *   green = BUY / profit / protected
 *   red   = SELL / loss / risk / LIVE / EMERGENCY_STOP
 *   amber = warning / SUSPENDED / DEGRADED / SCALING_REVIEW
 *   blue  = info
 *   gray  = IDLE
 */

export type Tone =
  | "buy"
  | "sell"
  | "warning"
  | "info"
  | "idle"
  | "neutral"
  | "live";

/** Tailwind classes for a soft "pill" surface in the given tone. */
export function toneBadge(tone: Tone): string {
  switch (tone) {
    case "buy":
      return "bg-buy-soft text-buy border-buy/30";
    case "sell":
      return "bg-sell-soft text-sell border-sell/30";
    case "warning":
      return "bg-warning-soft text-warning border-warning/30";
    case "info":
      return "bg-info-soft text-info border-info/30";
    case "live":
      return "bg-live/15 text-live border-live/40";
    case "idle":
      return "bg-idle-soft text-muted-foreground border-border-strong";
    case "neutral":
    default:
      return "bg-surface-2 text-foreground border-border-strong";
  }
}

/** Foreground (text/icon) colour utility for a tone. */
export function toneText(tone: Tone): string {
  switch (tone) {
    case "buy":
      return "text-buy";
    case "sell":
      return "text-sell";
    case "warning":
      return "text-warning";
    case "info":
      return "text-info";
    case "live":
      return "text-live";
    case "idle":
      return "text-muted-foreground";
    default:
      return "text-foreground";
  }
}

/** Solid dot/background colour utility for a tone. */
export function toneDot(tone: Tone): string {
  switch (tone) {
    case "buy":
      return "bg-buy";
    case "sell":
      return "bg-sell";
    case "warning":
      return "bg-warning";
    case "info":
      return "bg-info";
    case "live":
      return "bg-live";
    case "idle":
      return "bg-idle";
    default:
      return "bg-muted-foreground";
  }
}

export type Bias = "BUY" | "SELL";
export function biasTone(bias: Bias): Tone {
  return bias === "BUY" ? "buy" : "sell";
}

export type PositionRole =
  | "INITIALE"
  | "RENFORT"
  | "EXTENSION"
  | "DEFENSE"
  | "OPERATEUR";

export const ROLE_META: Record<
  PositionRole,
  { label: string; tone: Tone; help: string }
> = {
  INITIALE: {
    label: "Initiale",
    tone: "info",
    help: "Première position du cycle — l'entrée manuelle P1 (BUY ou SELL) qui ouvre le two-trail V6. Sa direction est le biais fixe de tout le cycle.",
  },
  RENFORT: {
    label: "Renfort",
    tone: "buy",
    help: "Renfort même sens sur rebond de swing — ouvert à +r du creux (dip-bounce), pas du dernier fill. Plafonné à maxSide.",
  },
  OPERATEUR: {
    label: "Opérateur",
    tone: "warning",
    help: "Entrée forcée par l'opérateur (« Acheter maintenant ») — même taille, même stop, mêmes gates §7 qu'un renfort, mais sans attendre le rebond de swing ; recentre le swing. Étiquetée pour rester mesurable.",
  },
  EXTENSION: {
    label: "Extension",
    tone: "neutral",
    help: "Rôle de renfort V2 historique ; plus émis par PROTECTED_LADDER_V6. Conservé pour les positions passées.",
  },
  DEFENSE: {
    label: "Défense",
    tone: "warning",
    help: "Position défensive du sens opposé sur repli de swing. HISTORIQUE (défense retirée 2026-07-16) : plus jamais émise par le moteur. Conservée pour relire les positions passées.",
  },
};

export type ProtectedState =
  | "INITIAL"
  | "BREAK_EVEN"
  | "BREAK_EVEN_PLUS"
  | "TRAILING";

/**
 * V6 stop-protection ladder shown by the ProtectionStepper: INITIAL (SL ratchet) →
 * TRAILING (armed). Stops only ever advance (SPEC §7/§18). The legacy V2 break-even
 * states stay in {@link ProtectedState} + {@link PROTECTION_META} for historical
 * rendering, but are NOT on this ladder — PROTECTED_LADDER_V6 never emits them.
 */
export const PROTECTION_STEPS: ProtectedState[] = ["INITIAL", "TRAILING"];

export const PROTECTION_META: Record<
  ProtectedState,
  { label: string; short: string; tone: Tone; help: string }
> = {
  INITIAL: {
    label: "Stop initial",
    short: "Initial",
    tone: "idle",
    help: "Stop au cliquet du SL catastrophe — il avance quand des positions s'ajoutent, ne s'élargit jamais ; plein risque SL tant que le trail n'est pas armé.",
  },
  BREAK_EVEN: {
    label: "Break-even",
    short: "Break-even",
    tone: "info",
    help: "Historique (échelle V2) : stop à l'entrée. V6 va d'INITIAL à TRAILING et n'émet jamais cet état ; affiché pour les positions passées.",
  },
  BREAK_EVEN_PLUS: {
    label: "Break-even +",
    short: "BE +",
    tone: "buy",
    help: "Historique (échelle V2) : stop au-delà de l'entrée. Plus émis par V6 ; affiché pour les positions passées.",
  },
  TRAILING: {
    label: "Trailing",
    short: "Trail",
    tone: "buy",
    help: "Le stop suit l'extrême à r_trail — armé quand le côté atteint n ET latent ≥ n·r_trail (sortie trail ≥ 0 hors gap). Les stops ne s'élargissent jamais.",
  },
};

export function protectionStepIndex(state: ProtectedState): number {
  return PROTECTION_STEPS.indexOf(state);
}

export type SystemState =
  | "HEALTHY"
  | "DEGRADED"
  | "SAFE_SUSPENDED"
  | "SCALING_REVIEW";

export const SYSTEM_STATE_META: Record<
  SystemState,
  { label: string; tone: Tone }
> = {
  HEALTHY: { label: "HEALTHY", tone: "buy" },
  DEGRADED: { label: "DEGRADED", tone: "warning" },
  SAFE_SUSPENDED: { label: "SAFE_SUSPENDED", tone: "warning" },
  SCALING_REVIEW: { label: "SCALING_REVIEW", tone: "warning" },
};
